import { useCallback, useEffect, useMemo, useState } from "react";
import {
  LegacyCard as Card,
  Text,
  Button,
  Checkbox,
  TextField,
  Thumbnail,
  Spinner,
  InlineStack,
  BlockStack,
  Modal,
  Select,
} from "@shopify/polaris";
import {
  createApiClient,
  ShopifyProduct,
  type CatalogCreatePayload,
  type Template,
} from "../lib/api";

interface Props {
  api: ReturnType<typeof createApiClient>;
  onCreateCatalog: (payload: CatalogCreatePayload) => Promise<void>;
  onNotice?: (message: string) => void;
}

// 1x1 transparent gif so imageless products still render a tidy thumbnail slot.
const BLANK_IMAGE =
  "data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==";
const MAX_CATALOG_PRODUCTS = 250;

function formatPrice(product: ShopifyProduct): string {
  const price = product.priceRange?.minVariantPrice;
  if (!price) return "";
  return `${price.amount} ${price.currencyCode}`;
}

interface ProductSelectionSummaryProps {
  selectedCount: number;
  onCreate: () => void;
}

export function ProductSelectionSummary({
  selectedCount,
  onCreate,
}: ProductSelectionSummaryProps) {
  const productsOverLimit = Math.max(selectedCount - MAX_CATALOG_PRODUCTS, 0);
  const selectionOverLimit = productsOverLimit > 0;

  return (
    <BlockStack gap="200">
      <InlineStack align="space-between" blockAlign="center">
        <Text as="span" variant="bodyMd">
          {selectedCount} of {MAX_CATALOG_PRODUCTS} selected
        </Text>
        <Button
          variant="primary"
          onClick={onCreate}
          disabled={selectionOverLimit}
        >
          Create catalog from selected
        </Button>
      </InlineStack>
      {selectionOverLimit ? (
        <Text as="p" variant="bodySm" tone="critical">
          Catalogs can include up to {MAX_CATALOG_PRODUCTS} products. Deselect{" "}
          {productsOverLimit} product{productsOverLimit === 1 ? "" : "s"} to
          continue.
        </Text>
      ) : (
        <Text as="p" variant="bodySm" tone="subdued">
          Catalogs support up to {MAX_CATALOG_PRODUCTS} products.
        </Text>
      )}
    </BlockStack>
  );
}

function unavailableProductsMessage(count: number): string {
  return count === 1
    ? "1 unavailable product was hidden because it has no variants available for sale."
    : `${count} unavailable products were hidden because they have no variants available for sale.`;
}

export function ProductBrowser({ api, onCreateCatalog, onNotice }: Props) {
  const [query, setQuery] = useState("");
  const [activeQuery, setActiveQuery] = useState("");
  const [products, setProducts] = useState<ShopifyProduct[]>([]);
  const [unavailableProductCount, setUnavailableProductCount] = useState(0);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selectingAll, setSelectingAll] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [catalogTitle, setCatalogTitle] = useState("Product catalog");
  const [catalogDescription, setCatalogDescription] = useState("");
  const [templates, setTemplates] = useState<Template[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templatesError, setTemplatesError] = useState<string | null>(null);
  const [templateId, setTemplateId] = useState("");
  const [creating, setCreating] = useState(false);

  const templateOptions = useMemo(
    () =>
      templates.map((template) => ({
        label: template.name,
        value: String(template.id),
      })),
    [templates],
  );
  const selectedTemplateId = Number(templateId);
  const hasValidTemplate =
    Number.isInteger(selectedTemplateId) && selectedTemplateId > 0;
  const selectionOverLimit = selectedIds.size > MAX_CATALOG_PRODUCTS;
  const allResultsSelected =
    products.length > 0 &&
    !hasNextPage &&
    products.every((product) => selectedIds.has(product.id));
  const selectAllState = allResultsSelected
    ? true
    : selectedIds.size > 0
      ? "indeterminate"
      : false;

  const notifyUnavailableProducts = useCallback(
    (count: number) => {
      if (count > 0) {
        onNotice?.(unavailableProductsMessage(count));
      }
    },
    [onNotice],
  );

  const fetchPage = useCallback(
    async (opts: { query?: string; cursor?: string; append: boolean }) => {
      if (opts.append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      setError(null);
      try {
        const page = await api.getProducts({
          query: opts.query,
          cursor: opts.cursor,
        });
        setProducts((prev) =>
          opts.append ? [...prev, ...page.products] : page.products,
        );
        const hiddenCount = page.unavailableProductCount ?? 0;
        setUnavailableProductCount((prev) =>
          opts.append ? prev + hiddenCount : hiddenCount,
        );
        notifyUnavailableProducts(hiddenCount);
        setCursor(page.pageInfo.endCursor);
        setHasNextPage(page.pageInfo.hasNextPage);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to load products",
        );
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [api, notifyUnavailableProducts],
  );

  useEffect(() => {
    void fetchPage({ append: false });
  }, [fetchPage]);

  useEffect(() => {
    let active = true;

    async function fetchTemplates() {
      setTemplatesLoading(true);
      setTemplatesError(null);
      try {
        const nextTemplates = await api.listTemplates();
        if (!active) return;

        setTemplates(nextTemplates);
        const initialTemplate =
          nextTemplates.find((template) => template.is_default) ??
          nextTemplates[0];
        setTemplateId(initialTemplate ? String(initialTemplate.id) : "");
        if (nextTemplates.length === 0) {
          setTemplatesError(
            "No templates available yet — catalogs will use the default product layout.",
          );
        }
      } catch (err) {
        if (!active) return;

        // Template listing is optional — a failure (e.g. the endpoint isn't
        // deployed yet) just means catalogs render with the default layout.
        // Log the real error for debugging but don't alarm the user.
        console.warn("Template list unavailable:", err);
        setTemplates([]);
        setTemplateId("");
        setTemplatesError(
          "Templates unavailable — catalogs will use the default product layout.",
        );
      } finally {
        if (active) {
          setTemplatesLoading(false);
        }
      }
    }

    void fetchTemplates();
    return () => {
      active = false;
    };
  }, [api]);

  const runSearch = useCallback(() => {
    const nextQuery = query.trim();
    setSelectedIds(new Set());
    setProducts([]);
    setUnavailableProductCount(0);
    setCursor(null);
    setHasNextPage(false);
    setActiveQuery(nextQuery);
    void fetchPage({ query: nextQuery || undefined, append: false });
  }, [fetchPage, query]);

  const loadMore = useCallback(() => {
    if (!cursor) return;
    void fetchPage({ query: activeQuery || undefined, cursor, append: true });
  }, [fetchPage, cursor, activeQuery]);

  const toggleSelectAll = useCallback(
    async (checked: boolean) => {
      if (!checked) {
        setSelectedIds(new Set());
        return;
      }

      setSelectingAll(true);
      setError(null);

      let nextProducts = [...products];
      let nextCursor = cursor;
      let morePages = hasNextPage;
      const visitedCursors = new Set<string>();

      try {
        while (morePages) {
          if (!nextCursor || visitedCursors.has(nextCursor)) {
            throw new Error("Could not load every product. Please try again.");
          }
          visitedCursors.add(nextCursor);

          const page = await api.getProducts({
            query: activeQuery || undefined,
            cursor: nextCursor,
          });
          const productsById = new Map(
            nextProducts.map((product) => [product.id, product]),
          );
          page.products.forEach((product) =>
            productsById.set(product.id, product),
          );
          const hiddenCount = page.unavailableProductCount ?? 0;
          setUnavailableProductCount((prev) => prev + hiddenCount);
          notifyUnavailableProducts(hiddenCount);
          nextProducts = Array.from(productsById.values());
          nextCursor = page.pageInfo.endCursor;
          morePages = page.pageInfo.hasNextPage;

          if (nextProducts.length >= MAX_CATALOG_PRODUCTS) {
            const selectedProducts = nextProducts.slice(
              0,
              MAX_CATALOG_PRODUCTS,
            );
            setProducts(nextProducts);
            setCursor(nextCursor);
            setHasNextPage(morePages);
            setSelectedIds(
              new Set(selectedProducts.map((product) => product.id)),
            );
            if (morePages || nextProducts.length > MAX_CATALOG_PRODUCTS) {
              setError(
                `Selected the first ${MAX_CATALOG_PRODUCTS} products. Refine your search to select a different set.`,
              );
            }
            return;
          }
        }

        setProducts(nextProducts);
        setCursor(nextCursor);
        setHasNextPage(false);
        setSelectedIds(new Set(nextProducts.map((product) => product.id)));
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to select all products",
        );
      } finally {
        setSelectingAll(false);
      }
    },
    [
      activeQuery,
      api,
      cursor,
      hasNextPage,
      notifyUnavailableProducts,
      products,
    ],
  );

  const toggle = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else if (next.size < MAX_CATALOG_PRODUCTS) {
        next.add(id);
      }
      return next;
    });
  }, []);

  const confirmCreate = useCallback(async () => {
    if (selectionOverLimit) {
      setError(`Catalogs can include up to ${MAX_CATALOG_PRODUCTS} products.`);
      return;
    }

    setCreating(true);
    setError(null);
    try {
      await onCreateCatalog({
        title: catalogTitle.trim() || "Product catalog",
        ...(catalogDescription.trim()
          ? { description: catalogDescription.trim() }
          : {}),
        // template_id is optional; when no template is picked the platform renders
        // its default product layout.
        ...(hasValidTemplate ? { template_id: selectedTemplateId } : {}),
        productIds: Array.from(selectedIds),
      });
      setModalOpen(false);
      setSelectedIds(new Set());
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create catalog");
    } finally {
      setCreating(false);
    }
  }, [
    onCreateCatalog,
    catalogTitle,
    catalogDescription,
    hasValidTemplate,
    selectedTemplateId,
    selectedIds,
    selectionOverLimit,
  ]);

  return (
    <>
      <Card>
        <Card.Header title="Shopify products" />
        <Card.Section>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              runSearch();
            }}
          >
            <TextField
              label="Search products"
              labelHidden
              value={query}
              onChange={setQuery}
              placeholder="Search products by title…"
              type="search"
              autoComplete="off"
              connectedRight={
                <Button submit loading={loading} disabled={selectingAll}>
                  Search
                </Button>
              }
            />
          </form>
        </Card.Section>

        {!loading && products.length > 0 && (
          <Card.Section subdued>
            <Checkbox
              label={
                selectingAll
                  ? "Selecting all products…"
                  : activeQuery
                    ? "Select all search results"
                    : "Select all products"
              }
              checked={selectAllState}
              disabled={selectingAll || loadingMore}
              onChange={(checked) => void toggleSelectAll(checked)}
            />
          </Card.Section>
        )}

        {selectedIds.size > 0 && (
          <Card.Section subdued>
            <ProductSelectionSummary
              selectedCount={selectedIds.size}
              onCreate={() => setModalOpen(true)}
            />
          </Card.Section>
        )}

        <section
          aria-label="Shopify product results"
          style={{ maxHeight: "32rem", overflowY: "auto" }}
          tabIndex={0}
        >
          {loading && (
            <Card.Section>
              <Spinner accessibilityLabel="Loading products" />
            </Card.Section>
          )}

          {!loading && products.length === 0 && (
            <Card.Section>
              <Text tone="subdued" as="p">
                {unavailableProductCount > 0
                  ? "No products currently available for sale were found."
                  : "No products found."}
              </Text>
            </Card.Section>
          )}

          {!loading &&
            products.map((product) => (
              <Card.Section key={product.id}>
                <InlineStack gap="300" blockAlign="center">
                  <Checkbox
                    label={product.title}
                    labelHidden
                    checked={selectedIds.has(product.id)}
                    disabled={
                      selectingAll ||
                      (!selectedIds.has(product.id) &&
                        selectedIds.size >= MAX_CATALOG_PRODUCTS)
                    }
                    onChange={() => toggle(product.id)}
                  />
                  <Thumbnail
                    source={product.featuredImage?.url || BLANK_IMAGE}
                    alt={product.featuredImage?.altText || product.title}
                    size="small"
                  />
                  <BlockStack gap="050">
                    <Text variant="bodyMd" fontWeight="semibold" as="p">
                      {product.title}
                    </Text>
                    {formatPrice(product) && (
                      <Text variant="bodySm" tone="subdued" as="p">
                        {formatPrice(product)}
                      </Text>
                    )}
                  </BlockStack>
                </InlineStack>
              </Card.Section>
            ))}

          {hasNextPage && !loading && (
            <Card.Section>
              <Button
                onClick={loadMore}
                loading={loadingMore}
                disabled={selectingAll}
                fullWidth
              >
                Load more
              </Button>
            </Card.Section>
          )}

          {error && (
            <Card.Section>
              <Text tone="critical" as="p">
                {error}
              </Text>
            </Card.Section>
          )}
        </section>
      </Card>

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title="Create catalog"
        primaryAction={{
          content: "Create catalog",
          onAction: () => void confirmCreate(),
          loading: creating,
          // template is optional (default layout when none) — only block on
          // no products selected or templates still loading.
          disabled:
            selectedIds.size === 0 || selectionOverLimit || templatesLoading,
        }}
        secondaryActions={[
          { content: "Cancel", onAction: () => setModalOpen(false) },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="300">
            <Text as="p" tone="subdued">
              {selectedIds.size} product{selectedIds.size === 1 ? "" : "s"} will
              be included.
            </Text>
            <TextField
              label="Catalog title"
              value={catalogTitle}
              onChange={setCatalogTitle}
              autoComplete="off"
            />
            <TextField
              label="Catalog description (optional)"
              value={catalogDescription}
              onChange={setCatalogDescription}
              autoComplete="off"
              multiline={3}
            />
            <Select
              label="Template"
              options={templateOptions}
              value={templateId}
              onChange={setTemplateId}
              disabled={templatesLoading || templateOptions.length === 0}
            />
            {templatesLoading && (
              <Spinner accessibilityLabel="Loading templates" />
            )}
            {templatesError && (
              <Text tone="subdued" as="p">
                {templatesError}
              </Text>
            )}
          </BlockStack>
        </Modal.Section>
      </Modal>
    </>
  );
}
